import React, { useEffect } from "react";
import styled from "styled-components";
import { Row, Col, Card, Typography, Button } from "antd";
import {
  UserOutlined,
  SmileOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  DollarOutlined,
  AppstoreAddOutlined,
  TeamOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import Miximageall from "../image/bannermiximges.webp";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";

const { Title, Paragraph } = Typography;

// Styled Components
const BannerContainer = styled.div`
  margin-top: 80px;
  padding: 50px;
  background-color: #fff;

  @media (max-width: 768px) {
    padding: 30px;
    text-align: center;
  }
`;

const ImageWrapper = styled.div`
  display: inline-block;
  padding: 8px;
  background: linear-gradient(135deg, #00a650, #00c1d4);
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
`;

const Image = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.03);
  }

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0 auto;
  }
`;

const ShopButton = styled(Button)`
  margin-top: 20px;
  border-radius: 20px;
  padding: 10px 20px;
  font-weight: bold;
  border: 2px solid #00a650;
  color: #00a650;

  &:hover {
    background: #00a650;
    color: white;
  }
`;

const AboutContainer = styled.div`
  padding: 50px;
  background-color: #f9f9f9;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const SectionTitle = styled(Title)`
  text-align: center;
  margin-bottom: 20px;
`;

const StyledCard = styled(Card)`
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const IconStyle = {
  fontSize: "40px",
  marginBottom: "10px",
};

const features = [
  {
    title: "Industry-Specific Expertise",
    desc: "Our team understands the unique procurement needs and operational challenges across your industry.",
    icon: <TeamOutlined style={{ ...IconStyle, color: "#ff85c0" }} />,
  },
  {
    title: "Trusted Supplier Network",
    desc: "Work with thoroughly vetted wholesalers, manufacturers, and exporters to ensure consistent quality and delivery.",
    icon: <AppstoreAddOutlined style={{ ...IconStyle, color: "#1890ff" }} />,
  },
  {
    title: "Affordable Wholesale Rates",
    desc: "Unlock exclusive access to industry-best pricing, volume discounts, and value-added deals.",
    icon: <DollarOutlined style={{ ...IconStyle, color: "#52c41a" }} />,
  },
  {
    title: "Global Access, Local Expertise",
    desc: "We operate on a global scale, but provide regional support for efficient communication and delivery.",
    icon: <GlobalOutlined style={{ ...IconStyle, color: "#faad14" }} />,
  },
  {
    title: "All-in-One Wholesale Platform",
    desc: "Manage procurement across multiple industries from a single reliable platform — hassle-free and efficient.",
    icon: <TagsOutlined style={{ ...IconStyle, color: "#722ed1" }} />,
  },
];

const AboutSection = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <BannerContainer>
      <Row gutter={[32, 32]} align="middle">
        {/* Image Column */}
        <Col xs={24} md={12} data-aos="fade-up" style={{ textAlign: "center" }}>
          <motion.div whileTap={{ scale: 0.95 }}>
            <ImageWrapper>
              <Image src={Miximageall} alt="Sunglasses" />
            </ImageWrapper>
          </motion.div>
        </Col>

        {/* Text Content Column */}
        <Col xs={24} md={12}>
          <Title level={2}>About Us</Title>
          <Paragraph
            style={{ fontSize: "17px", fontFamily: "sans-serif" }}
            data-aos="fade-up"
          >
            <motion.div whileTap={{ scale: 1.0 }}>
              At <b>Nexus Wealth Services</b>, we specialize in delivering
              comprehensive wholesale procurement solutions across key sectors
              including the Hotel Industry, Export-Import, Textile, and Real
              Estate. With a commitment to excellence and a deep understanding
              of multi-industry demands, we act as your strategic sourcing
              partner.
              <br />
              <br />
              <b>💡 Our Vision</b>  <br />To become a trusted name in multi-sector business by
              delivering excellence, building strong partnerships, and
              contributing to economic growth. 
              <br />
              <br />
              <b>🤝 Our Mission </b> <br /> • To serve
              customers with transparency and dedication  <br />• To build lasting
              relationships in every industry we operate  <br />• To grow sustainably
              and ethically in global markets
            </motion.div>
          </Paragraph>
        </Col>
      </Row>

      {/* Features Section */}
      <AboutContainer>
        <motion.div whileTap={{ scale: 1.0 }} data-aos="fade-up">
          <SectionTitle level={3}>Why Choose Us?</SectionTitle>
          <Row gutter={[24, 24]} justify="center">
            {/* <Col xs={24} sm={12} md={8} lg={8}>
              <StyledCard hoverable>
                <SmileOutlined style={{ ...IconStyle, color: "#ff7a45" }} />
                <Title level={4}>Happy Clients</Title>
                <Paragraph>We ensure top-notch service quality.</Paragraph>
              </StyledCard>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <StyledCard hoverable>
                <CheckCircleOutlined style={{ ...IconStyle, color: "#52c41a" }} />
                <Title level={4}>Proven Track Record</Title>
                <Paragraph>
                  We have successfully delivered 100+ projects.
                </Paragraph>
              </StyledCard>
            </Col> */}

            {features.map((item, index) => (
              <Col xs={24} sm={12} md={12} lg={8} key={index}>
                <StyledCard hoverable>
                  {item.icon}
                  <Title level={4}>{item.title}</Title>
                  <Paragraph>{item.desc}</Paragraph>
                </StyledCard>
              </Col>
            ))}
          </Row>
        </motion.div>
      </AboutContainer>
    </BannerContainer>
  );
};

export default AboutSection;
